/**
 * Risposte AI: l'AI prepara bozze di risposta pronte per le mail da rispondere.
 * L'utente rivede (modificabile), invia in un tap, o scarta.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Loader2, Send, X, Inbox, RefreshCw, Pencil, Check } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { FollowUpSection } from '@/components/inbox/FollowUpSection';
import { Button } from '@/components/ui/button';
import { aiRepliesApi, type AiReplySuggestion } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';

function fromLabel(f: AiReplySuggestion['from_address']): string {
  return f?.name || f?.email || 'Mittente sconosciuto';
}

export function AiRepliesPage() {
  const qc = useQueryClient();
  const listQuery = useQuery({ queryKey: ['ai-replies'], queryFn: () => aiRepliesApi.list() });
  const suggestions = useMemo(() => listQuery.data?.suggestions ?? [], [listQuery.data]);

  // testo modificabile per ogni card
  const [texts, setTexts] = useState<Record<string, string>>({});
  const textFor = (s: AiReplySuggestion) => texts[s.id] ?? s.reply_text;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['ai-replies'] });
    qc.invalidateQueries({ queryKey: ['messages'] });
  };

  const generateMut = useMutation({
    mutationFn: () => aiRepliesApi.generate(),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['ai-replies'] });
      toast.success(r.created > 0 ? `${r.created} risposte preparate` : 'Nessuna nuova mail da rispondere');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Generazione fallita'),
  });

  const sendMut = useMutation({
    mutationFn: (s: AiReplySuggestion) => aiRepliesApi.send(s.id, textFor(s)),
    onSuccess: () => { invalidate(); toast.success('Risposta inviata'); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Errore durante l'invio"),
  });

  const dismissMut = useMutation({
    mutationFn: (id: string) => aiRepliesApi.dismiss(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-replies'] }),
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Errore'),
  });

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Risposte AI</h1>
              <p className="text-sm text-muted-foreground">Bozze pronte per le email che richiedono risposta. Rivedi e invia.</p>
            </div>
            <Button onClick={() => generateMut.mutate()} disabled={generateMut.isPending}>
              {generateMut.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              Genera risposte
            </Button>
          </div>

          <FollowUpSection />

          {listQuery.isLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Inbox className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <div className="text-sm">Nessuna risposta in attesa.</div>
              <div className="text-xs mt-1">Premi “Genera risposte” o aspetta: l'AI le prepara man mano che arriva la posta.</div>
            </div>
          ) : (
            <div className="space-y-4">
              {suggestions.map((s) => {
                const edited = texts[s.id] !== undefined && texts[s.id] !== s.reply_text;
                return (
                <div key={s.id} className="rounded-lg border overflow-hidden">
                  {/* messaggio originale */}
                  <div className="bg-muted/40 px-4 py-2.5 border-b">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{s.subject || '(senza oggetto)'}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          da {fromLabel(s.from_address)} · a {s.account_email}
                        </div>
                      </div>
                      <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                        {s.received_at ? formatDistanceToNow(new Date(s.received_at), { addSuffix: true, locale: it }) : ''}
                      </span>
                    </div>
                    {s.snippet && <div className="text-xs text-muted-foreground/80 mt-1 line-clamp-2">{s.snippet}</div>}
                  </div>

                  {/* bozza AI modificabile */}
                  <div className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-primary">
                        <Sparkles className="h-3 w-3" /> Bozza AI
                      </div>
                      {edited ? (
                        <span className="flex items-center gap-1 text-[11px] font-medium text-emerald-600"><Check className="h-3 w-3" /> Modificata</span>
                      ) : (
                        <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><Pencil className="h-3 w-3" /> Modificabile</span>
                      )}
                    </div>
                    <textarea
                      value={textFor(s)}
                      onChange={(e) => setTexts((p) => ({ ...p, [s.id]: e.target.value }))}
                      rows={6}
                      placeholder="Bozza generata dall'AI — modificala come vuoi prima di inviare"
                      className={`w-full text-sm rounded-md border bg-background px-3 py-2 outline-none focus:ring-1 focus:ring-primary leading-relaxed transition-colors ${edited ? 'border-emerald-500/50' : ''}`}
                    />
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => dismissMut.mutate(s.id)} disabled={dismissMut.isPending}>
                        <X className="h-4 w-4 mr-1" /> Scarta
                      </Button>
                      <Button size="sm" onClick={() => sendMut.mutate(s)} disabled={sendMut.isPending || !textFor(s).trim()}>
                        {sendMut.isPending && sendMut.variables?.id === s.id
                          ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          : <Send className="h-4 w-4 mr-1" />}
                        Invia
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
